import { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Clock, LogOut, ExternalLink, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { API } from '../lib/constants';
import { useAuth } from '../context/AuthContext';
import { Sidebar, NAV } from '../components/dashboard/Sidebar';
import { StatCards, UploadPanel } from '../components/dashboard/OverviewPanels';
import { AttendeesTable } from '../components/dashboard/AttendeesTable';
import { PhotosGrid } from '../components/dashboard/PhotosGrid';
import { BrandingSettings } from '../components/dashboard/BrandingSettings';
import { BillingPanel } from '../components/dashboard/BillingPanel';
import { PrivacyPanel } from '../components/dashboard/PrivacyPanel';
import { PhotographersPanel } from '../components/dashboard/PhotographersPanel';
import { OutboxCard } from '../components/dashboard/OutboxCard';
import { BackMenu, DemoBanner } from '../components/dashboard/BackMenu';
import { QuickNav, SectionNav } from '../components/dashboard/SectionNav';

const PendingApproval = ({ tenant, onLogout }) => (
  <div className="min-h-screen bg-ink grid-bg flex items-center justify-center px-4">
    <div className="card-brutal p-8 max-w-md w-full text-center fade-up" data-testid="pending-approval-card">
      <span className="w-12 h-12 bg-amber-400 text-ink flex items-center justify-center mx-auto mb-5"><Clock className="w-6 h-6" /></span>
      <h2 className="h-display text-2xl">Awaiting approval</h2>
      <p className="text-sm text-slate-400 mt-3 leading-relaxed">
        <span className="font-semibold text-white">{tenant?.name || 'Your workspace'}</span> is pending review. A platform administrator will activate it shortly.
      </p>
      <span className="badge-warning mt-5 capitalize">{tenant?.approval_status || 'pending'}</span>
      <div className="mt-7 flex flex-col sm:flex-row gap-3 justify-center">
        <Link to="/" className="btn-ghost !py-3" data-testid="pending-home-btn">Back to Spotfie</Link>
        <button data-testid="pending-logout-btn" onClick={onLogout} className="btn-ghost !py-3"><LogOut className="w-4 h-4" /> Log out</button>
      </div>
    </div>
  </div>
);

const AdminDashboard = () => {
  const { account, tenant, logout, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [tab, setTabState] = useState('overview');
  const setTab = (id) => { setTabState(id); window.scrollTo({ top: 0, behavior: 'smooth' }); };
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [userToDelete, setUserToDelete] = useState(null);
  const [openCheckout, setOpenCheckout] = useState(false);

  const isApproved = tenant?.approval_status === 'approved';

  const fetchData = useCallback(async () => {
    try {
      const [s, u, i] = await Promise.all([axios.get(`${API}/admin/stats`), axios.get(`${API}/admin/users`), axios.get(`${API}/admin/images`)]);
      setStats(s.data); setUsers(u.data); setImages(i.data);
    } catch (e) {
      toast.error('Failed to load dashboard data');
    }
  }, []);

  useEffect(() => { if (isApproved) fetchData(); }, [isApproved, fetchData]);

  const handleUpload = async () => {
    setUploading(true);
    try {
      const fd = new FormData();
      selectedFiles.forEach((f) => fd.append('files', f));
      const { data } = await axios.post(`${API}/admin/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`${data.uploaded_count} photo(s) uploaded`);
      setSelectedFiles([]);
      fetchData();
    } catch (e) {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleProcess = async () => {
    setProcessing(true);
    try {
      await axios.post(`${API}/admin/process`);
      toast.success('Face matching started');
      setTimeout(() => { fetchData(); setProcessing(false); }, 3000);
    } catch (e) {
      toast.error('Processing failed');
      setProcessing(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/admin/user/${userToDelete.id}`);
      toast.success('Attendee removed');
      setUserToDelete(null);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleLogout = async () => { await logout(); navigate('/login'); };

  if (!isApproved) return <PendingApproval tenant={tenant} onLogout={handleLogout} />;

  const goUpgrade = () => { setTab('billing'); setOpenCheckout(true); };
  const title = NAV.find((n) => n.id === tab)?.label;

  return (
    <div className="min-h-screen bg-ink flex flex-col lg:flex-row" data-testid="admin-dashboard">
      <Sidebar tab={tab} onTab={setTab} tenant={tenant} account={account} onLogout={handleLogout} />
      <div className="flex-1 min-w-0">
        <header className="glass-header !z-30">
          <div className="px-6 lg:px-10 h-16 flex items-center justify-between gap-4">
            <div className="min-w-0">
              <h1 className="h-display text-xl">{title}</h1>
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 truncate" data-testid="tenant-name-badge">{tenant?.name} · {account?.email}</p>
            </div>
            <div className="flex items-center gap-2">
              <BackMenu tenantSlug={tenant?.slug} onLogout={handleLogout} />
              <span className={`hidden sm:inline-flex ${tenant?.plan === 'paid' ? 'badge-lime' : 'badge-muted'}`} data-testid="plan-badge">
                {tenant?.plan === 'paid' ? 'Pro Event Pass' : 'Free'}
              </span>
              <button onClick={fetchData} className="icon-btn" title="Refresh" data-testid="refresh-btn"><RefreshCw className="w-4 h-4" /></button>
              <a href={`/t/${tenant?.slug}`} target="_blank" rel="noreferrer" className="icon-btn lg:hidden" data-testid="header-public-link"><ExternalLink className="w-4 h-4" /></a>
            </div>
          </div>
        </header>

        {tenant?.is_demo && <DemoBanner />}

        <main className="px-6 lg:px-10 py-8 space-y-6 max-w-7xl">
          {tab === 'overview' && (
            <>
              <StatCards stats={stats} />
              <UploadPanel
                selectedFiles={selectedFiles}
                onSelect={(e) => setSelectedFiles(Array.from(e.target.files))}
                onUpload={handleUpload}
                onProcess={handleProcess}
                uploading={uploading}
                processing={processing}
                pendingCount={stats?.pending_images}
                onAutoProcessed={fetchData}
              />
              <QuickNav onTab={setTab} />
            </>
          )}
          {tab === 'attendees' && (<><AttendeesTable users={users} tenantSlug={tenant?.slug} onDelete={setUserToDelete} /><OutboxCard /></>)}
          {tab === 'photographers' && <PhotographersPanel />}
          {tab === 'photos' && <PhotosGrid images={images} />}
          {tab === 'branding' && <BrandingSettings tenant={tenant} onUpgrade={goUpgrade} />}
          {tab === 'billing' && <BillingPanel tenant={tenant} openCheckout={openCheckout} onCheckoutHandled={() => setOpenCheckout(false)} />}
          {tab === 'privacy' && <PrivacyPanel isDemo={!!tenant?.is_demo} onChanged={() => { fetchData(); checkAuth(); }} />}
          <SectionNav tab={tab} onTab={setTab} />
        </main>
      </div>

      <AlertDialog open={!!userToDelete} onOpenChange={(o) => !o && setUserToDelete(null)}>
        <AlertDialogContent className="bg-surface border-2 border-red-500 rounded-none shadow-[0_18px_40px_rgba(224,69,123,0.18)]">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display uppercase tracking-tight text-white">Delete {userToDelete?.name}?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              This removes their registration and face signature, and unlinks them from every matched photo. This can't be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel-btn" className="btn-ghost rounded-none">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="delete-confirm-btn" onClick={handleDelete} className="rounded-none bg-red-500 hover:bg-red-600 text-white font-display font-bold uppercase tracking-wider">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminDashboard;
